import ContentSkeleton from '@/components/ContentSkeleton';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Component, lazy, Suspense, createContext, useContext, useEffect, useState } from 'react';
import { ThemeProvider } from 'next-themes';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { Toaster } from '@/components/ui/sonner';
import RouteSkeleton from '@/components/RouteSkeleton';
import { canAccessAppPath, getRoleHomePath } from '@/lib/role-routing';
import '@/App.css';

const ThemePreferenceContext = createContext({
  themePreference: 'auto',
  setThemePreference: () => {},
  cycleThemePreference: () => {},
});

const THEME_PREFERENCE_KEY = 'themePreference';

function getThemeByHour(hour) {
  return hour >= 18 || hour < 6 ? 'dark' : 'light';
}

function getStoredThemePreference() {
  if (typeof window === 'undefined') return 'auto';
  const preference = localStorage.getItem(THEME_PREFERENCE_KEY);
  return preference === 'light' || preference === 'dark' || preference === 'auto' ? preference : 'auto';
}

function ThemePreferenceProvider({ children }) {
  const [themePreference, setThemePreferenceState] = useState(() => getStoredThemePreference());
  const [currentHour, setCurrentHour] = useState(() => new Date().getHours());

  useEffect(() => {
    const nextHourCheck = setInterval(() => {
      setCurrentHour(new Date().getHours());
    }, 60 * 1000);

    return () => clearInterval(nextHourCheck);
  }, []);

  const setThemePreference = (nextPreference) => {
    const safePreference = nextPreference === 'light' || nextPreference === 'dark' || nextPreference === 'auto' ? nextPreference : 'auto';
    setThemePreferenceState(safePreference);
    localStorage.setItem(THEME_PREFERENCE_KEY, safePreference);
  };

  const cycleThemePreference = () => {
    const orderedPreferences = ['auto', 'light', 'dark'];
    const currentIndex = orderedPreferences.indexOf(themePreference);
    const nextPreference = orderedPreferences[(currentIndex + 1) % orderedPreferences.length];
    setThemePreference(nextPreference);
  };

  const effectiveTheme = themePreference === 'auto' ? getThemeByHour(currentHour) : themePreference;

  return (
    <ThemePreferenceContext.Provider value={{ themePreference, setThemePreference, cycleThemePreference }}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} forcedTheme={effectiveTheme}>
        {children}
      </ThemeProvider>
    </ThemePreferenceContext.Provider>
  );
}

export function useThemePreference() {
  return useContext(ThemePreferenceContext);
}

const LandingPage = lazy(() => import('@/pages/LandingPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const TrialExpiredPage = lazy(() => import('@/pages/TrialExpiredPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ClientsPage = lazy(() => import('@/pages/ClientsPage'));
const ClientDetailPage = lazy(() => import('@/pages/ClientDetailPage'));
const DeliverablesPage = lazy(() => import('@/pages/DeliverablesPage'));
const DeliverableEditorPage = lazy(() => import('@/pages/DeliverableEditorPage'));
const TaskEditorPage = lazy(() => import('@/pages/TaskEditorPage'));
const PaperworkPage = lazy(() => import('@/pages/PaperworkPage'));
const HRMSPage = lazy(() => import('@/pages/HRMSPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ReviewPage = lazy(() => import('@/pages/ReviewPage'));
const PortalLoginPage = lazy(() => import('@/pages/PortalLoginPage'));
const PortalDashboardPage = lazy(() => import('@/pages/PortalDashboardPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ComingSoonPage = lazy(() => import('@/pages/ComingSoonPage'));
const SalesPage = lazy(() => import('@/pages/SalesPage'));
const SalesLeadsPage = lazy(() => import('@/pages/SalesLeadsPage'));
const SalesTeamPage = lazy(() => import('@/pages/SalesTeamPage'));
const SalesSettingsPage = lazy(() => import('@/pages/SalesSettingsPage'));
const SalesProposalsPage = lazy(() => import('@/pages/SalesProposalsPage'));
const SalesQuotationsPage = lazy(() => import('@/pages/SalesQuotationsPage'));
const FormSharePage = lazy(() => import('@/pages/FormSharePage'));
const SalesFollowUpsPage = lazy(() => import('@/pages/SalesFollowUpsPage'));
const SalesPipelinePage = lazy(() => import('@/pages/SalesPipelinePage'));
const SalesLeadDetailPage = lazy(() => import('@/pages/SalesLeadDetailPage'));
const SalesDealDetailPage = lazy(() => import('@/pages/SalesDealDetailPage'));
const SalesDealsPage = lazy(() => import('@/pages/SalesDealsPage'));
const SalesProposalSharePage = lazy(() => import('@/pages/SalesProposalSharePage'));
const TeamAllocationPage = lazy(() => import('@/pages/TeamAllocationPage'));
const ClientPortalProjectsPage = lazy(() => import('@/pages/ClientPortalProjectsPage'));
const CommercialsPage = lazy(() => import('@/pages/CommercialsPage'));
const ReviewsPage = lazy(() => import('@/pages/ReviewsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const EmployeeDashboardPage = lazy(() => import('@/pages/EmployeeDashboardPage'));
const ProjectManagerDashboardPage = lazy(() => import('@/pages/ProjectManagerDashboardPage'));
const TeamMemberWorkbenchPage = lazy(() => import('@/pages/TeamMemberWorkbenchPage'));
const FinanceDashboardPage = lazy(() => import('@/pages/FinanceDashboardPage'));
const SalesExecutiveDashboardPage = lazy(() => import('@/pages/SalesExecutiveDashboardPage'));
const LeadGeneratorDashboardPage = lazy(() => import('@/pages/LeadGeneratorDashboardPage'));
const PlatformAdminPage = lazy(() => import('@/pages/PlatformAdminPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const UserTermsPage = lazy(() => import('@/pages/UserTermsPage'));
const WorkspaceLayout = lazy(() => import('@/components/Layout'));

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'This page could not be rendered.' };
  }

  componentDidUpdate(prevProps) {
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.setState({ hasError: false, message: '' });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <Suspense fallback={<RouteSkeleton workspace label="Recovering workspace" />}>
        <WorkspaceLayout>
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6">
            <h1 className="text-xl font-semibold text-destructive">Page recovered from an error</h1>
            <p className="mt-2 text-sm text-muted-foreground">The workspace shell stayed available. Refresh this page or go back to Projects to continue.</p>
            <p className="mt-3 rounded bg-background p-3 text-xs text-muted-foreground">{this.state.message}</p>
            <div className="mt-4 flex gap-2">
              <button className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground" onClick={() => window.location.reload()}>Refresh page</button>
              <button className="rounded border px-4 py-2 text-sm" onClick={() => { window.location.href = '/projects'; }}>Open Projects</button>
            </div>
          </div>
        </WorkspaceLayout>
      </Suspense>
    );
  }
}

function PageLoader({ message = 'Loading page...' }) { return <RouteSkeleton label={message} />; }

function RoleHomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader message="Loading workspace..." />;
  return <Navigate to={getRoleHomePath(user)} replace />;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = window.location.pathname;
  if (loading) return <PageLoader message="Loading workspace..." />;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Check trial expiration
  const trialStatus = user?.trial_status;
  if (trialStatus && !trialStatus.valid && trialStatus.reason === 'trial_expired') {
    return <Navigate to="/trial-expired" replace />;
  }

  if (!canAccessAppPath(user, location)) {
    if (location === getRoleHomePath(user)) return <div role="alert" className="p-6">You do not have access to this workspace. Contact your agency administrator.</div>;
    return <Navigate to={getRoleHomePath(user)} replace />;
  }

  return <RouteErrorBoundary resetKey={location}><Suspense fallback={<RouteSkeleton workspace label="Loading workspace" />}><WorkspaceLayout><Suspense fallback={<ContentSkeleton header label="Loading workspace" />}>{children}</Suspense></WorkspaceLayout></Suspense></RouteErrorBoundary>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<RouteSkeleton label="Loading page" />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={isAuthenticated ? <RoleHomeRedirect /> : <LandingPage />} />
        <Route path="/login" element={isAuthenticated ? <RoleHomeRedirect /> : <LoginPage />} />
        <Route path="/signup" element={isAuthenticated ? <RoleHomeRedirect /> : <SignupPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/trial-expired" element={<TrialExpiredPage />} />
        <Route path="/review/:token" element={<ReviewPage />} />
        <Route path="/sales/proposals/share/:token" element={<SalesProposalSharePage />} />
        <Route path="/forms/:token" element={<FormSharePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<UserTermsPage />} />
        <Route path="/user-terms" element={<Navigate to="/terms" replace />} />

        {/* Client Portal routes (public) */}
        <Route path="/portal/:agencySlug" element={<PortalLoginPage />} />
        <Route path="/portal/:agencySlug/dashboard" element={<PortalDashboardPage />} />
        <Route path="/client-portal" element={<ClientPortalProjectsPage />} />

        {/* Protected routes */}
        <Route path="/platform-admin" element={<PlatformAdminPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/employee-dashboard" element={<ProtectedRoute><EmployeeDashboardPage /></ProtectedRoute>} />
        <Route path="/project-manager-dashboard" element={<ProtectedRoute><ProjectManagerDashboardPage /></ProtectedRoute>} />
        <Route path="/team-member-workbench" element={<ProtectedRoute><TeamMemberWorkbenchPage /></ProtectedRoute>} />
        <Route path="/finance-dashboard" element={<ProtectedRoute><FinanceDashboardPage /></ProtectedRoute>} />
        <Route path="/sales-executive-dashboard" element={<ProtectedRoute><SalesExecutiveDashboardPage /></ProtectedRoute>} />
        <Route path="/lead-generator-dashboard" element={<ProtectedRoute><LeadGeneratorDashboardPage /></ProtectedRoute>} />
        <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
        <Route path="/clients/:id" element={<ProtectedRoute><ClientDetailPage /></ProtectedRoute>} />
        <Route path="/deliverables" element={<ProtectedRoute><DeliverablesPage /></ProtectedRoute>} />
        <Route path="/deliverables/:id" element={<ProtectedRoute><DeliverableEditorPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
        <Route path="/templates" element={<Navigate to="/projects" replace />} />
        <Route path="/paperwork" element={<ProtectedRoute><PaperworkPage /></ProtectedRoute>} />
        <Route path="/hrms" element={<ProtectedRoute><HRMSPage /></ProtectedRoute>} />
        <Route path="/hrms/documents" element={<ProtectedRoute><HRMSPage /></ProtectedRoute>} />
        <Route path="/hrms/documents/new" element={<ProtectedRoute><HRMSPage /></ProtectedRoute>} />
        <Route path="/hrms/employees" element={<Navigate to="/team" replace />} />
        <Route path="/hrms/employees/:id" element={<Navigate to="/team" replace />} />
        <Route path="/hrms/settings" element={<Navigate to="/settings?tab=agency-profile" replace />} />
        <Route path="/hrms/payroll" element={<ProtectedRoute><HRMSPage /></ProtectedRoute>} />
        <Route path="/hrms/history" element={<ProtectedRoute><HRMSPage /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

        <Route path="/sales" element={<ProtectedRoute><SalesPage /></ProtectedRoute>} />
        <Route path="/sales/lead-capture" element={<ProtectedRoute><SalesLeadsPage /></ProtectedRoute>} />
        <Route path="/sales/leads" element={<ProtectedRoute><SalesLeadsPage /></ProtectedRoute>} />
        <Route path="/sales/leads/new" element={<ProtectedRoute><SalesLeadsPage /></ProtectedRoute>} />
        <Route path="/sales/leads/:id" element={<ProtectedRoute><SalesLeadDetailPage /></ProtectedRoute>} />
        <Route path="/sales/contacts" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
        <Route path="/sales/contacts/:id" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
        <Route path="/sales/companies" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
        <Route path="/sales/companies/:id" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
        <Route path="/sales/deals" element={<Navigate to="/sales" replace />} />
        <Route path="/sales/deals/:id" element={<Navigate to="/sales" replace />} />
        <Route path="/sales/pipeline" element={<Navigate to="/sales" replace />} />
        <Route path="/sales/activities" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
        <Route path="/sales/follow-ups" element={<Navigate to="/sales/leads" replace />} />
        <Route path="/sales/proposals" element={<ProtectedRoute><SalesProposalsPage /></ProtectedRoute>} />
        <Route path="/sales/quotations" element={<ProtectedRoute><SalesQuotationsPage /></ProtectedRoute>} />
        <Route path="/sales/objections" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
        <Route path="/sales/settings" element={<ProtectedRoute><SalesSettingsPage /></ProtectedRoute>} />
        <Route path="/sales/team" element={<ProtectedRoute><SalesTeamPage /></ProtectedRoute>} />
        <Route path="/commercials" element={<ProtectedRoute><CommercialsPage /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><TeamAllocationPage /></ProtectedRoute>} />
        <Route path="/projects/dashboard" element={<Navigate to="/projects" replace />} />
        <Route path="/projects/team-allocation" element={<ProtectedRoute><TeamAllocationPage mode="allocation" /></ProtectedRoute>} />
        <Route path="/projects/tasks" element={<ProtectedRoute><TeamAllocationPage mode="tasks" /></ProtectedRoute>} />
        <Route path="/projects/tasks/:taskId" element={<ProtectedRoute><TaskEditorPage /></ProtectedRoute>} />
        <Route path="/projects/leave-availability" element={<ProtectedRoute><TeamAllocationPage mode="leave" /></ProtectedRoute>} />
        <Route path="/projects/timeline" element={<ProtectedRoute><TeamAllocationPage mode="timeline" /></ProtectedRoute>} />
        <Route path="/projects/archived" element={<ProtectedRoute><TeamAllocationPage mode="archived" /></ProtectedRoute>} />
        <Route path="/projects/:projectId/allocation" element={<Navigate to="/projects/team-allocation" replace />} />
        <Route path="/projects/:projectId/tasks" element={<Navigate to="/projects/tasks" replace />} />
        <Route path="/projects/:projectId/timeline" element={<Navigate to="/projects/timeline" replace />} />
        <Route path="/workload" element={<Navigate to="/projects" replace />} />
        <Route path="/team/leave" element={<Navigate to="/projects" replace />} />
        <Route path="/admin/leave-approvals" element={<Navigate to="/projects" replace />} />
        <Route path="/reviews" element={<ProtectedRoute><ReviewsPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemePreferenceProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemePreferenceProvider>
  );
}

export default App;
